import { LEGEND } from "@/shared/components/gantt-carga/gantt-carga.constants";
import { useEffect, useMemo, useRef, useState } from "react";

import { getErrorMessage } from "@/core/services";
import { formatMonthRangeLabel, resolveListEmptyKind, useToast } from "@/ds";
import type { FilterValue } from "@/ds";
import { getPersistedUser } from "@/modules/auth";
import {
  applyCargaFilters,
  buildCargaFilterColumns,
  buildTimeline,
  CARGA_FILTER,
  EMPTY_CARGA_TRABALHO_FILTERS,
  exportCargaTrabalhoObras,
  exportCargaTrabalhoProfissionais,
  filtraObraCancelada,
  hasCargaFilters,
  isObraCancelada,
  persistIndicators,
  readStoredIndicators,
  GANTT_INDICATOR_OPTIONS,
  todayIso,
  useCargaTrabalho,
} from "@/modules/carga-trabalho";
import type {
  CargaTrabalhoAgrupamento,
  CargaTrabalhoConflitoIgnorado,
  CargaTrabalhoData,
  CargaTrabalhoObrasData,
  CargaTrabalhoProfissionaisData,
  GanttIndicators,
} from "@/modules/carga-trabalho";
import { describeReportFilters, formatExportedLabel, panelQuery } from "@/modules/export";
import type { EquipeObraTarget } from "@/modules/recursos";
import {
  aplicarCenarioObras,
  aplicarCenarioProfissionais,
  ausenciasDoProfissional,
  cargaDeObras,
  cargaDeProfissionais,
  conflitosObrasProfissionais,
  conflitosProfissionais,
  incluirAlteracao,
  novosConflitos,
  remanejarNaEquipeSimulada,
  SEM_MARCAS,
} from "@/modules/recursos/simulacao";
import type {
  AlteracaoSimulada,
  ConflitoNovo,
  MarcasSimulacao,
} from "@/modules/recursos/simulacao";

import {
  chipsDosFiltros,
  conteudoDoVazio,
  datasDoPeriodo,
  VAZIO_BUSCA,
  VAZIO_FILTRO,
} from "../carga-de-trabalho.utils";
import {
  CARGA_CHIP_TEST_ID_PREFIX,
  CARGA_ESTADO_IDS,
  CARGA_LEGEND_TEST_ID,
  CARGA_TOOLBAR_IDS,
  CARGA_PROFISSIONAIS_SAVED_FILTERS_SCREEN,
  CARGA_PROFISSIONAIS_SEARCH_PLACEHOLDER,
} from "../carga-de-trabalho.constants";
import {
  IGNORAR_SUCCESS_MESSAGE,
  REMANEJAR_SUCCESS_MESSAGE,
} from "../hooks/use-acoes-conflito-profissional";
import type {
  ResolucaoProfissionalSimulada,
  ResolverConflitoOutcome,
} from "../components/resolver-conflito";
import {
  buildObraGroups,
  focoDaVisaoPorObras,
} from "@/shared/components/gantt-carga/gantt-obras-rows";
import { buildProfissionalRows } from "@/shared/components/gantt-carga/gantt-rows";
import type { GanttRowModel } from "@/shared/components/gantt-carga/gantt-rows";
import { nomeDoPapel } from "../hooks/nome-do-papel";
import { useCargaExportacao } from "../hooks/use-carga-exportacao";
import { useRotulosDasObras } from "../hooks/use-rotulos-das-obras";
import { useCargaFiltrosSalvos } from "../hooks/use-carga-filtros-salvos";
import { usePeriodoDaAba } from "../hooks/use-carga-periodo";
import type { DefinirEquipeSimulacao } from "../types-carga-de-trabalho";
import {
  AGRUPAMENTO_OPTIONS,
  GROUPING_HINT,
  VAZIO_SEM_ALOCACAO,
} from "./carga-profissionais.constants";
import type {
  CargaProfissionaisProps,
  CargaProfissionaisViewModel,
} from "./types-carga-profissionais";

/** Carga de Trabalho: perspectiva Profissionais (US-1779), agrupada por Profissionais ou por Obras. */
export function useCargaProfissionais({
  periodoCompartilhado,
  onPerspectivaChange,
  onOpenPapeis,
  onDefinirEquipe,
  onEditarEquipe,
  reloadToken = 0,
  onNotify,
  cenario = null,
  onEntrarSimulacao,
  onAbrirHistorico,
  onIncluirNaSimulacao,
  onCargaAtual,
}: CargaProfissionaisProps): CargaProfissionaisViewModel {
  const toast = useToast();
  const simulando = cenario !== null;
  const [agrupamento, setAgrupamento] = useState<CargaTrabalhoAgrupamento>("profissionais");
  const { periodo: periodoMes, escolherPeriodo } = usePeriodoDaAba(periodoCompartilhado);
  const periodo = useMemo(() => datasDoPeriodo(periodoMes), [periodoMes]);
  const [search, setSearch] = useState("");
  const [filterValue, setFilterValue] = useState<FilterValue>({});
  const [indicators, setIndicators] = useState<GanttIndicators>(() => readStoredIndicators());
  const filtrosSalvos = useCargaFiltrosSalvos(
    CARGA_PROFISSIONAIS_SAVED_FILTERS_SCREEN,
    aplicarFiltros,
  );
  const [indicatorsOpen, setIndicatorsOpen] = useState(false);
  const [resolverRow, setResolverRow] = useState<GanttRowModel | null>(null);
  // RN07: conflitos ignorados nesta sessão seguem sinalizados mesmo antes do bff refleti-los.
  const [ignoradosLocais, setIgnoradosLocais] = useState<CargaTrabalhoConflitoIgnorado[]>([]);

  const filters = useMemo(
    () => ({ ...EMPTY_CARGA_TRABALHO_FILTERS, busca: search.trim() }),
    [search],
  );

  const result = useCargaTrabalho({ agrupamento, periodo, filters });
  const { status, data, errorMessage, reload } = result;
  const isProfissionais = result.agrupamento === "profissionais";

  const lastReloadToken = useRef(reloadToken);
  useEffect(() => {
    if (lastReloadToken.current === reloadToken) return;
    lastReloadToken.current = reloadToken;
    reload();
  }, [reloadToken, reload]);

  useEffect(() => {
    if (!onCargaAtual) return;
    if (!data) {
      onCargaAtual(null);
      return;
    }
    onCargaAtual(
      isProfissionais
        ? cargaDeProfissionais(data as CargaTrabalhoProfissionaisData)
        : cargaDeObras(data as CargaTrabalhoObrasData),
    );
  }, [data, onCargaAtual, isProfissionais]);

  // Na simulação o Gantt mostra os dados com o cenário aplicado (HU 2225).
  const simulado = useMemo((): { dados: CargaTrabalhoData | null; marcas: MarcasSimulacao } => {
    if (!data || !cenario) return { dados: data, marcas: SEM_MARCAS };
    return isProfissionais
      ? aplicarCenarioProfissionais(data as CargaTrabalhoProfissionaisData, cenario)
      : aplicarCenarioObras(data as CargaTrabalhoObrasData, cenario);
  }, [data, cenario, isProfissionais]);

  // As opções vêm dos dados sem filtro; a filtragem do painel é feita sobre eles, no cliente.
  const filterColumns = useMemo(() => buildCargaFilterColumns(simulado.dados), [simulado.dados]);
  const rotulosDasObras = useRotulosDasObras(filterColumns);
  const filteredData = useMemo(
    () => (simulado.dados ? applyCargaFilters(simulado.dados, filterValue) : null),
    [simulado.dados, filterValue],
  );

  const today = useMemo(() => todayIso(), []);
  // A escala é o período escolhido: o cabeçalho mostra só os meses dele (as barras além das pontas são cortadas).
  // Na visão por Obras a rolagem abre na 1ª alocação visível do período; por Profissionais, no início dele.
  const foco = useMemo(() => {
    if (!filteredData || !("obras" in filteredData)) return periodo.inicio;
    const alocacoes = filteredData.obras.flatMap((obra) =>
      obra.profissionais.map(({ inicio, fim }) => ({
        inicio,
        fim,
        obraCancelada: isObraCancelada(obra),
      })),
    );
    return focoDaVisaoPorObras(alocacoes, periodo, indicators.cancelada);
  }, [filteredData, periodo, indicators.cancelada]);
  const timeline = useMemo(() => buildTimeline(periodo, foco), [periodo, foco]);

  const rows = useMemo(() => {
    if (status !== "success" || !filteredData || !isProfissionais) return [];
    return buildProfissionalRows(
      filteredData as CargaTrabalhoProfissionaisData,
      timeline,
      indicators,
      today,
      ignoradosLocais,
    );
  }, [status, filteredData, isProfissionais, timeline, indicators, today, ignoradosLocais]);

  const groups = useMemo(() => {
    if (status !== "success" || !filteredData || isProfissionais) return [];
    return buildObraGroups(
      filteredData as CargaTrabalhoObrasData,
      timeline,
      indicators,
      today,
      ignoradosLocais,
    );
  }, [status, filteredData, isProfissionais, timeline, indicators, today, ignoradosLocais]);

  const itemCount = isProfissionais ? rows.length : groups.length;
  const isEmpty = status === "success" && itemCount === 0;

  const hasSearch = search.trim() !== "";
  const hasFilters = hasCargaFilters(filterValue);
  const emptyKind = resolveListEmptyKind(hasSearch, hasFilters);
  const periodoLabel = formatMonthRangeLabel(periodoMes);
  const reportFilters = describeReportFilters(
    search,
    filterColumns,
    filterValue,
    { periodo: periodoLabel },
    rotulosDasObras,
  );
  const printTitle = isProfissionais
    ? "Carga de Trabalho — Profissionais"
    : "Carga de Trabalho — Obras (profissionais)";

  const { exportView, exportar } = useCargaExportacao({
    fileName: "carga-profissionais",
    isEmpty,
    loadFile: (format) => {
      const exportFn = isProfissionais
        ? exportCargaTrabalhoProfissionais
        : exportCargaTrabalhoObras;
      return exportFn({
        periodo,
        busca: search,
        format,
        filtros: reportFilters,
        painel: {
          ...panelQuery(filterValue, [
            { key: CARGA_FILTER.cargo, param: "filtroCargo", multiple: true },
            { key: CARGA_FILTER.obra, param: "filtroObra", multiple: true },
            { key: CARGA_FILTER.fase, param: "filtroFase", multiple: true },
            { key: CARGA_FILTER.conflito, param: "filtroConflito", multiple: false },
            {
              key: CARGA_FILTER.indisponibilidade,
              param: "filtroIndisponibilidade",
              multiple: false,
            },
            { key: CARGA_FILTER.cancelada, param: "filtroCancelada", multiple: false },
          ]),
          // A tela oculta as canceladas com o indicador desligado (HU 1779 RN13); o arquivo segue a tela.
          ...(indicators.cancelada ? {} : { filtroCancelada: "nao" }),
        },
      });
    },
    auditoria: {
      recurso: isProfissionais ? "alocacoes/profissionais" : "alocacoes/obras/profissionais",
      quantidade: itemCount,
      inicio: periodo.inicio,
      fim: periodo.fim,
      busca: search,
    },
    printTitle,
  });

  // Cada indicador desligado some da legenda; Indisponibilidade só existe na visão por Profissionais (Figma).
  const legendItems = LEGEND.filter(
    (item) =>
      indicators[item.key as keyof GanttIndicators] &&
      (isProfissionais || item.key !== "indisponibilidade"),
  );

  /** Conflitos em aberto que a alteração criaria, pela regra de conflito desta perspectiva (RN011). */
  function conflitosNovosDe(alteracao: AlteracaoSimulada): ConflitoNovo[] {
    if (!data || !cenario) return [];
    const depois = incluirAlteracao(cenario, alteracao);

    if (isProfissionais) {
      const base = data as CargaTrabalhoProfissionaisData;
      return novosConflitos(
        conflitosProfissionais(aplicarCenarioProfissionais(base, cenario).dados),
        conflitosProfissionais(aplicarCenarioProfissionais(base, depois).dados),
      );
    }

    const base = data as CargaTrabalhoObrasData;
    return novosConflitos(
      conflitosObrasProfissionais(aplicarCenarioObras(base, cenario).dados),
      conflitosObrasProfissionais(aplicarCenarioObras(base, depois).dados),
    );
  }

  /** Sem `papelObraId` (ou com a lista de papéis fora), o Papel fica de fora da alteração. */
  async function nomeDoPapelOuAviso(papelObraId: string | null): Promise<string | undefined> {
    try {
      return await nomeDoPapel(papelObraId);
    } catch (error) {
      toast.warn(
        "Não foi possível carregar os papéis.",
        `${getErrorMessage(error)} A alteração segue sem o nome do papel.`,
      );
      return undefined;
    }
  }

  /** Resolver Conflito na simulação (HU 2224): remanejar e ignorar viram alterações do cenário. */
  function resolucaoSimulada(row: GanttRowModel): ResolucaoProfissionalSimulada | undefined {
    if (!cenario || !onIncluirNaSimulacao) return undefined;
    const incluir = onIncluirNaSimulacao;
    const { profissional } = row;
    const atual = { id: profissional.id, nome: profissional.nome, time: profissional.time };

    return {
      remanejar: async ({ alocacao, substituto, justificativa }) => {
        const novo = {
          id: substituto.id,
          nome: substituto.nome || substituto.email,
          time: substituto.time || null,
        };
        // Alocação de uma equipe simulada: troca-se o profissional na própria equipe.
        const remanejada = remanejarNaEquipeSimulada(cenario, alocacao.id, novo, justificativa) ?? {
          acao: "remanejar-profissional" as const,
          papel: await nomeDoPapelOuAviso(alocacao.papelObraId),
          obra: alocacao.obra,
          alocacaoId: alocacao.id,
          periodo: { inicio: alocacao.inicio, fim: alocacao.fim },
          anterior: atual,
          novo,
        };
        const alteracao: AlteracaoSimulada =
          remanejada.acao === "definir-equipe" ? remanejada : { ...remanejada, justificativa };
        return incluir({
          alteracao,
          conflitosNovos: [
            ...conflitosNovosDe(alteracao),
            ...ausenciasDoProfissional(substituto, alocacao),
          ],
        });
      },
      ignorar: ({ conflito, justificativa }) => {
        const alteracao: AlteracaoSimulada = {
          acao: "ignorar-conflito-profissional",
          profissional: atual,
          obras: [conflito.a.obra, conflito.b.obra],
          alocacaoIds: [conflito.a.id, conflito.b.id],
          conflito: { inicio: conflito.inicio, fim: conflito.fim },
          justificativa,
        };
        return incluir({ alteracao, conflitosNovos: [] });
      },
    };
  }

  /** Abre o modal de equipe; na simulação a equipe vem do fold, não da carga crua. */
  function abrirEquipe(
    abrir: CargaProfissionaisProps["onDefinirEquipe"],
    target: EquipeObraTarget,
    comAlocacaoId: boolean,
  ) {
    if (!abrir) return;
    if (!cenario) {
      abrir(target);
      return;
    }

    // O ⋮ só aparece na visão por Obras.
    const fonte = (simulado.dados ?? data) as CargaTrabalhoObrasData | null;
    const obra = fonte?.obras.find((item) => item.id === target.id);
    if (!obra) return;

    const { profissionais: equipeAtual, ...obraBase } = obra;
    const contexto: DefinirEquipeSimulacao = {
      obra: obraBase,
      anterior: equipeAtual.map((membro) => ({
        usuarioId: membro.id,
        nome: membro.nome,
        time: membro.time,
        papelObraId: membro.papelObraId ?? "",
        inicio: membro.inicio,
        fim: membro.fim,
        ...(comAlocacaoId && membro.alocacaoId ? { alocacaoId: membro.alocacaoId } : {}),
      })),
      conflitosNovos: conflitosNovosDe,
    };
    abrir(target, contexto);
  }

  function conflitoResolvido(outcome: ResolverConflitoOutcome) {
    setResolverRow(null);
    if (outcome.tipo === "ignorado") {
      setIgnoradosLocais((current) => [...current, { alocacaoIds: outcome.alocacaoIds }]);
    }
    onNotify?.({
      severity: "success",
      summary: "Sucesso",
      detail: outcome.tipo === "remanejado" ? REMANEJAR_SUCCESS_MESSAGE : IGNORAR_SUCCESS_MESSAGE,
    });
    reload();
  }

  /** Filtrar "Obra cancelada = Sim" liga o indicador Cancelada, senão a linha ficaria vazia. */
  function aplicarFiltros(next: FilterValue) {
    setFilterValue(next);
    if (filtraObraCancelada(next)) setIndicators((current) => ({ ...current, cancelada: true }));
  }

  function limparTudo() {
    setSearch("");
    setFilterValue({});
  }

  function removerChip(key: string) {
    setFilterValue((current) => ({ ...current, [key]: [] }));
  }

  function salvarIndicadores(next: GanttIndicators) {
    setIndicators(next);
    persistIndicators(next);
    setIndicatorsOpen(false);
  }

  const legendaImpressao = { items: legendItems, simulando, forPrint: true };

  return {
    estado: {
      status,
      errorMessage,
      onRetry: reload,
      isEmpty,
      emptyKind,
      emptyContent: conteudoDoVazio(emptyKind, {
        vazio: VAZIO_SEM_ALOCACAO,
        busca: VAZIO_BUSCA,
        filtro: VAZIO_FILTRO,
      }),
      onClearEmpty: emptyKind === "sem-resultados-busca" ? () => setSearch("") : limparTudo,
      ids: CARGA_ESTADO_IDS,
    },
    capaImpressao: {
      title: printTitle,
      generatedLabel: formatExportedLabel(getPersistedUser()?.name),
      filters: reportFilters,
      simulando,
    },
    legendaImpressao,
    toolbar: {
      ids: CARGA_TOOLBAR_IDS,
      search,
      onSearch: setSearch,
      searchLabel: "Buscar por profissional, obra ou embarcação",
      searchPlaceholder: CARGA_PROFISSIONAIS_SEARCH_PLACEHOLDER,
      searchMaxLength: 100,
      periodo: periodoMes,
      onPeriodo: escolherPeriodo,
      agrupamento: {
        value: agrupamento,
        options: AGRUPAMENTO_OPTIONS,
        hint: GROUPING_HINT,
        onChange: (value) => setAgrupamento(value as CargaTrabalhoAgrupamento),
      },
      filtros: {
        definitions: filterColumns,
        value: filterValue,
        onChange: aplicarFiltros,
        ...filtrosSalvos,
      },
      perspectiva: "profissionais",
      onPerspectiva: onPerspectivaChange,
      menu: {
        simulando,
        onEntrarSimulacao,
        onAbrirHistorico,
        onPapeis: onOpenPapeis,
        canExport: status === "success" && !isEmpty,
        onExportar: exportView.open,
        onIndicadores: () => setIndicatorsOpen(true),
      },
    },
    chips: chipsDosFiltros(filterColumns, filterValue, CARGA_CHIP_TEST_ID_PREFIX, rotulosDasObras),
    removerChip,
    legenda: {
      items: legendItems,
      testId: CARGA_LEGEND_TEST_ID,
      simulando,
      alteracoes: cenario?.alteracoes.length ?? 0,
    },
    ganttProfissionais:
      status === "success" && rows.length > 0
        ? {
            rows,
            timeline,
            today,
            headerLabel: "Profissional",
            // Na simulação, sem como incluir no cenário, não há resolução (ela gravaria).
            onResolverConflito: simulando && !onIncluirNaSimulacao ? undefined : setResolverRow,
            marcas: simulado.marcas,
          }
        : null,
    ganttObras:
      status === "success" && groups.length > 0
        ? {
            groups,
            timeline,
            today,
            onDefinirEquipe: onDefinirEquipe
              ? (target) => abrirEquipe(onDefinirEquipe, target, false)
              : undefined,
            onEditarEquipe: onEditarEquipe
              ? (target) => abrirEquipe(onEditarEquipe, target, true)
              : undefined,
            marcas: simulado.marcas,
          }
        : null,
    resolver: resolverRow
      ? {
          row: resolverRow,
          carga: isProfissionais ? (simulado.dados as CargaTrabalhoProfissionaisData | null) : null,
          periodo,
          today,
          canceladaVisivel: indicators.cancelada,
          onResolved: simulando ? () => setResolverRow(null) : conflitoResolvido,
          onClose: () => setResolverRow(null),
          simulacao: resolucaoSimulada(resolverRow),
        }
      : null,
    exportacao: {
      aberta: exportView.isOpen,
      exportando: exportView.isExporting,
      exportar,
      fechar: exportView.close,
    },
    indicadores: {
      opcoes: GANTT_INDICATOR_OPTIONS,
      aberto: indicatorsOpen,
      valor: indicators,
      salvar: salvarIndicadores,
      cancelar: () => setIndicatorsOpen(false),
    },
  };
}
